/**
 * Stable mode: persist fetch target (priority list + selected underlyings) in sessionStorage
 * so each browser tab keeps its own 30s refresh target. sessionStorage is empty → fall back to
 * localStorage once for migration / Legacy RTL (which only uses localStorage).
 */

import { isFuturesModeEnabled } from './unifiedSymbolMapping';

const KEY_UNDERLYINGS = 'mmec_selected_underlyings';
const KEY_PRIMARY = 'mmec_selected_underlying';
const KEY_PRIORITY = 'mmec_priority_fetch_symbols';

function parseUnderlyingsList(raw: string | null): string[] {
  if (!raw) return [];
  try {
    const arr = JSON.parse(raw);
    if (Array.isArray(arr) && arr.length > 0) {
      return arr.map((x: unknown) => String(x ?? '').trim()).filter(Boolean);
    }
  } catch {
    /* ignore */
  }
  return [];
}

function readUnderlyingsFromStorage(store: Storage): string[] {
  try {
    const fromArr = parseUnderlyingsList(store.getItem(KEY_UNDERLYINGS));
    if (fromArr.length > 0) return fromArr;
    const single = (store.getItem(KEY_PRIMARY) || '').trim();
    if (single) return [single];
    const fromPri = parseUnderlyingsList(store.getItem(KEY_PRIORITY));
    if (fromPri.length > 0) return fromPri;
  } catch {
    /* ignore */
  }
  return [];
}

/**
 * Session first, then localStorage (RTL and migration).
 */
export function readStableUnderlyingsSessionFirst(): string[] {
  if (typeof sessionStorage !== 'undefined') {
    const fromSession = readUnderlyingsFromStorage(sessionStorage);
    if (fromSession.length > 0) return fromSession;
  }
  if (typeof localStorage !== 'undefined') {
    return readUnderlyingsFromStorage(localStorage);
  }
  return [];
}

export function readStablePrimarySessionFirst(): string {
  const list = readStableUnderlyingsSessionFirst();
  return list[0] ? String(list[0]).trim() : '';
}

export function getPriorityFetchSymbols(): string[] {
  if (typeof sessionStorage !== 'undefined') {
    const fromSession = parseUnderlyingsList(sessionStorage.getItem(KEY_PRIORITY));
    if (fromSession.length > 0) return fromSession;
  }
  if (typeof localStorage !== 'undefined') {
    const fromLocal = parseUnderlyingsList(localStorage.getItem(KEY_PRIORITY));
    if (fromLocal.length > 0) return fromLocal;
  }
  return readStableUnderlyingsSessionFirst();
}

export function setStableTabSelection(symbols: string[]): void {
  if (typeof sessionStorage === 'undefined') return;
  const cleaned = Array.from(new Set(symbols.map((s) => String(s ?? '').trim()).filter(Boolean)));
  if (cleaned.length === 0) {
    sessionStorage.removeItem(KEY_UNDERLYINGS);
    sessionStorage.removeItem(KEY_PRIMARY);
    sessionStorage.removeItem(KEY_PRIORITY);
    return;
  }
  try {
    sessionStorage.setItem(KEY_UNDERLYINGS, JSON.stringify(cleaned));
    sessionStorage.setItem(KEY_PRIMARY, cleaned[0]);
    sessionStorage.setItem(KEY_PRIORITY, JSON.stringify(cleaned));
  } catch {
    /* quota / private mode */
  }
}

export function clearStableTabSelection(): void {
  if (typeof sessionStorage === 'undefined') return;
  try {
    sessionStorage.removeItem(KEY_UNDERLYINGS);
    sessionStorage.removeItem(KEY_PRIMARY);
    sessionStorage.removeItem(KEY_PRIORITY);
  } catch {
    /* ignore */
  }
}

/** True when user selected Stable (CBOE) as the data source. */
export function isStableDataSourceSelected(): boolean {
  if (typeof localStorage === 'undefined') return false;
  return (localStorage.getItem('mmec_data_source') || 'rtl_legacy') === 'stable';
}

/**
 * Stable prefetch path: chain is fetched as a snapshot; underlying and intraday data mode
 * must not be retuned like RTL unless Futures mode is on (then full filter behavior applies).
 */
export function isStablePrefetchFilterLock(): boolean {
  return isStableDataSourceSelected() && !isFuturesModeEnabled();
}
